import React from "react";
import { ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
function ElitePerformanceCategoriesConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="w-full">
            <AspectRatio ratio={3 / 4}>
              <div className="h-full w-full animate-pulse bg-muted rounded-xl" />
            </AspectRatio>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground mb-4">Unable to load categories.</p>
        <Button variant="outline" onClick={refetch}>
          Retry
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {terms.map((term, i) => <Link key={term.id} to={term.link || "#"} className="group relative block overflow-hidden rounded-xl bg-card" data-index={i}>
          <AspectRatio ratio={3 / 4}>
            {}
            <div className="h-full w-full overflow-hidden">
              <img src={term.image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/generic_sport_category.png"} alt={term.name} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" loading="lazy" />
            </div>

            {}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-0" />

            {}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-secondary/90 opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:opacity-100">
              <span className="translate-y-4 text-4xl font-bold text-secondary-foreground opacity-0 transition-all duration-500 delay-100 group-hover:translate-y-0 group-hover:opacity-100">
                {term.count || "120+"}
              </span>
              <span className="translate-y-4 text-sm font-medium uppercase tracking-widest text-secondary-foreground/80 opacity-0 transition-all duration-500 delay-150 group-hover:translate-y-0 group-hover:opacity-100">
                Items Available
              </span>
            </div>

            {}
            <div className="absolute bottom-0 left-0 p-6 transition-transform duration-500 group-hover:-translate-y-2">
              <h3 className="text-3xl font-bold uppercase tracking-tighter text-white group-hover:text-secondary-foreground transition-colors duration-300">
                {term.name}
              </h3>
              <div className="mt-2 h-1 w-12 bg-primary transition-all duration-500 group-hover:w-full group-hover:bg-secondary-foreground/50" />
            </div>
          </AspectRatio>
        </Link>)}
    </div>;
}
export default function ElitePerformanceCategories() {
  const wp_term_query_params = {
    taxonomy: "product_cat",
    per_page: 3,
    orderby: "count",
    order: "desc"
  };
  const wp_term_query = new WP_Term_Query(wp_term_query_params);
  return <section id="elite-performance-categories" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-12 flex flex-col items-end justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl">
              Elite <span className="text-primary">Categories</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Explore our curated collections designed for peak performance. 
              From track to court, find the gear that elevates your game.
            </p>
          </div>
          <Button variant="outline" className="hidden md:flex" asChild>
            <Link to="/performance-lab">
              View All Categories <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <EcommerceTermsProvider wp_term_query={wp_term_query} className="w-full">
          <ElitePerformanceCategoriesConsumer />
        </EcommerceTermsProvider>

        <div className="mt-8 flex justify-center md:hidden">
          <Button variant="outline" className="w-full" asChild>
            <Link to="/performance-lab">
              View All Categories <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}